// Product analytics — the launch-funnel "Growth" events (founder-named).
// Browser mirror of the server's lib/productEvents.js. The CLIENT owns the UX
// moments listed in ProductEventName; the SERVER owns account_created /
// DJ_activated / Fan_activated (it drops those names if a client sends them).
//
// Guarantees: never throws, never blocks UI, never retries (analytics, not
// money). No cookies. Identity = the same per-device anonymous id the A/B rails
// use (localStorage "requesta-anon-id") + a session id that rotates after 30
// idle minutes. Every API call also carries X-Rq-Anon / X-Rq-Session /
// X-Rq-Platform so server-emitted events stitch onto the same device journey.
//
// Attribution is first-touch: ?src= / utm_source (+ utm_campaign) on the first
// page load is remembered per device and replayed on every event. Launch
// channels mint tagged links (e.g. requesta.app/?src=instagram).

import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";
import api from "../api";
import { SERVER_URL } from "../config";
import { getAnonId } from "./abTesting";

export type ProductEventName =
  | "app_opened"
  | "landing_viewed"
  | "path_selected"
  | "signup_started"
  | "signup_step_completed"
  | "signup_failed"
  | "invite_code_viewed"
  | "invite_code_submitted"
  | "invite_code_failed"
  | "invite_code_accepted"
  | "onboarding_started"
  | "onboarding_step_completed"
  | "onboarding_completed"
  // A button in a talent lifecycle email was tapped (props.email = the
  // email's name from ?em=; lib/emailClick). Admin → Growth → Talent emails.
  | "email_clicked"
  // Fan checkout steps — song requests only (components/InlinePayOptions).
  | "checkout_viewed"
  | "pay_tapped"
  | "payment_failed";

export type SelectedPath = "dj" | "fan";
type Platform = "ios" | "android" | "web" | "desktop";

export interface TrackOptions {
  selectedPath?: SelectedPath;
  screen?: string;
  errorReason?: unknown;
  props?: Record<string, unknown>;
}

interface OutEvent {
  name: ProductEventName;
  anonymousId: string;
  sessionId: string;
  selectedPath: SelectedPath | null;
  source: string | null;
  campaign: string | null;
  platform: Platform;
  appVersion: string;
  timezone: string | null;
  screen: string | null;
  errorReason: string | null;
  props: Record<string, unknown> | null;
  clientAt: string;
}

const SESSION_KEY = "rq_sid";
const SESSION_AT_KEY = "rq_sid_at";
const SESSION_IDLE_MS = 30 * 60 * 1000;
const ATTRIB_KEY = "rq_attrib";
const PATH_KEY = "rq_selected_path";
const ONCE_KEY = "rq_ev_once";
const FLUSH_DELAY_MS = 800;
const MAX_BATCH = 20;
const MAX_QUEUE = 100;

// ---- storage (every call swallowed: private mode, blocked storage, SSR) -----

const ls = {
  get: (k: string): string | null => {
    try {
      return localStorage.getItem(k);
    } catch {
      return null;
    }
  },
  set: (k: string, v: string) => {
    try {
      localStorage.setItem(k, v);
    } catch {}
  },
};
const ss = {
  get: (k: string): string | null => {
    try {
      return sessionStorage.getItem(k);
    } catch {
      return null;
    }
  },
  set: (k: string, v: string) => {
    try {
      sessionStorage.setItem(k, v);
    } catch {}
  },
  del: (k: string) => {
    try {
      sessionStorage.removeItem(k);
    } catch {}
  },
};

function makeId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

// ---- device facts -----------------------------------------------------------

function detectPlatform(): Platform {
  try {
    if (Capacitor.isNativePlatform()) {
      const p = Capacitor.getPlatform();
      return p === "ios" || p === "android" ? p : "web";
    }
    if (typeof navigator !== "undefined" && /Electron/i.test(navigator.userAgent)) return "desktop";
  } catch {}
  return "web";
}

const platform: Platform = detectPlatform();
// Web bundle version from the build script (REACT_APP_VERSION=$npm_package_version).
// Native overwrites it with "1.1 (35)" once the App plugin answers — NEVER
// awaited (Capacitor plugin proxies can hang an await forever); events fired
// before it resolves simply carry the web marker.
let appVersion = process.env.REACT_APP_VERSION ? `web-${process.env.REACT_APP_VERSION}` : "web-dev";
if (platform === "ios" || platform === "android") {
  try {
    CapApp.getInfo()
      .then((info) => {
        if (info?.version) appVersion = `${info.version} (${info.build || "?"})`;
      })
      .catch(() => {});
  } catch {}
}

function timezone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

// ---- session ----------------------------------------------------------------

// Per-tab (web) / per-launch (native) session; rotates after 30 idle minutes.
// Rotation also clears the per-session "once" marks so landing/signup_started
// count again in a genuinely new visit.
export function touchSession(): { id: string; rotated: boolean } {
  const now = Date.now();
  let id = ss.get(SESSION_KEY);
  const at = Number(ss.get(SESSION_AT_KEY) || 0);
  let rotated = false;
  if (!id || !at || now - at > SESSION_IDLE_MS) {
    id = makeId();
    rotated = true;
    ss.set(SESSION_KEY, id);
    ss.del(ONCE_KEY);
  }
  ss.set(SESSION_AT_KEY, String(now));
  try {
    api.defaults.headers.common["X-Rq-Session"] = id;
  } catch {}
  return { id, rotated };
}

// ---- attribution (first touch) ----------------------------------------------

interface Attrib {
  source: string | null;
  campaign: string | null;
  medium: string | null;
  landing: string | null;
  at: number;
}

function readAttrib(): Attrib | null {
  try {
    const raw = ls.get(ATTRIB_KEY);
    return raw ? (JSON.parse(raw) as Attrib) : null;
  } catch {
    return null;
  }
}

function captureAttribution() {
  try {
    const q = new URLSearchParams(window.location.search);
    const clean = (v: string | null) => (v ? v.trim().slice(0, 64).toLowerCase() || null : null);
    const source = clean(q.get("src") || q.get("utm_source") || q.get("ref"));
    const campaign = clean(q.get("utm_campaign") || q.get("campaign"));
    const medium = clean(q.get("utm_medium"));
    const stored = readAttrib();
    // First touch wins — except an untagged first visit upgrades to the first
    // TAGGED one (a friend's text link opened after an earlier stray visit).
    if (stored && (stored.source || !source)) return;
    ls.set(
      ATTRIB_KEY,
      JSON.stringify({ source, campaign, medium, landing: window.location.pathname, at: Date.now() } as Attrib)
    );
  } catch {}
}

// ---- selected path ----------------------------------------------------------

export function getSelectedPath(): SelectedPath | null {
  const v = ls.get(PATH_KEY);
  return v === "dj" || v === "fan" ? v : null;
}
export function setSelectedPath(p: SelectedPath) {
  ls.set(PATH_KEY, p);
}

// ---- queue + flush ----------------------------------------------------------

const queue: OutEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;

function schedule() {
  if (timer) return;
  timer = setTimeout(() => {
    timer = null;
    void flush();
  }, FLUSH_DELAY_MS);
}

async function flush() {
  if (!queue.length) return;
  const batch = queue.splice(0, MAX_BATCH);
  try {
    await api.post("/events", { events: batch });
  } catch {
    /* dropped — analytics never retries */
  }
  if (queue.length) schedule();
}

// Synchronous best-effort send for page-hide / reload moments. Beacons can't
// set JSON content types or custom headers, so the body goes as text/plain
// (the server parses it) and each event carries its own ids.
export function flushNow() {
  try {
    if (!queue.length) return;
    const batch = queue.splice(0, queue.length);
    const body = JSON.stringify({ events: batch });
    const url = `${SERVER_URL}/api/events`;
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      if (navigator.sendBeacon(url, new Blob([body], { type: "text/plain" }))) return;
    }
    fetch(url, { method: "POST", body, headers: { "Content-Type": "text/plain" }, keepalive: true }).catch(() => {});
  } catch {}
}

// ---- init (runs once on import) ---------------------------------------------

let initialized = false;
function ensureInit() {
  if (initialized) return;
  initialized = true;
  try {
    api.defaults.headers.common["X-Rq-Anon"] = getAnonId();
    api.defaults.headers.common["X-Rq-Platform"] = platform;
  } catch {}
  touchSession();
  captureAttribution();
  try {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        flushNow();
        return;
      }
      // Back to the foreground after a long idle = a new "open" for the funnel
      // (native resume, or a tab left open overnight).
      if (touchSession().rotated) track("app_opened", { props: { via: "resume" } });
    });
    window.addEventListener("pagehide", flushNow);
  } catch {}
}

// ---- public API -------------------------------------------------------------

export function track(name: ProductEventName, opts: TrackOptions = {}) {
  try {
    // A browser driven by automation (tests, scrapers) is not a visitor.
    if (typeof navigator !== "undefined" && navigator.webdriver) return;
    ensureInit();
    const { id: sessionId } = touchSession();
    const attrib = readAttrib();
    queue.push({
      name,
      anonymousId: getAnonId(),
      sessionId,
      selectedPath: opts.selectedPath ?? getSelectedPath(),
      source: attrib?.source ?? null,
      campaign: attrib?.campaign ?? null,
      platform,
      appVersion,
      timezone: timezone(),
      screen: opts.screen ?? null,
      errorReason: opts.errorReason == null ? null : String(opts.errorReason).slice(0, 160),
      props: opts.props ?? null,
      clientAt: new Date().toISOString(),
    });
    if (queue.length > MAX_QUEUE) queue.splice(0, queue.length - MAX_QUEUE);
    schedule();
  } catch {}
}

// Fire at most once per session per key (landing_viewed, signup_started…).
export function trackOnce(name: ProductEventName, key: string, opts: TrackOptions = {}) {
  try {
    ensureInit();
    touchSession(); // may rotate → clears the once-set first
    const raw = ss.get(ONCE_KEY);
    const seen: string[] = raw ? JSON.parse(raw) : [];
    const k = `${name}:${key}`;
    if (seen.includes(k)) return;
    seen.push(k);
    ss.set(ONCE_KEY, JSON.stringify(seen.slice(-50)));
    track(name, opts);
  } catch {}
}

// Record the chosen side once per device and emit path_selected the first
// time it's known (role screen on native; inferred from destination on web).
export function selectPath(path: SelectedPath, via: string) {
  try {
    const known = getSelectedPath();
    setSelectedPath(path);
    if (known === path) return;
    track("path_selected", { selectedPath: path, props: { via } });
  } catch {}
}

ensureInit();
